import { COMPLETE_REASONS, STATUS, TIERS, gradeSubmission, type SubmissionData, type TierKey } from '@commpass/shared';
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { StaffTabs } from '../components/StaffTabs';
import { StatusPill } from '../components/StatusPill';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { useSubmissions, useUpdateSubmission } from '../data/submissions';
import type { SubmissionRow } from '../types';

type QueueView = 'board' | 'grid';
type SortMode = 'stale' | 'needby' | 'expiration' | 'newest';
// Sort choices shown as buttons above the board; applied within each lane.
const SORT_OPTIONS: { key: SortMode; label: string }[] = [
  { key: 'stale', label: 'Days since touched' },
  { key: 'needby', label: 'Quote need-by' },
  { key: 'expiration', label: 'Expiration' },
  { key: 'newest', label: 'Newest' },
];

// AAS staff who can own a submission on the board. Each gets a color for quick glancing.
const ASSIGNEES = ['Josh', 'Cameron', 'Cyndy', 'Kylie'] as const;
const ASSIGNEE_COLOR: Record<string, string> = {
  Josh: '#1B4CA1',
  Cameron: '#2E7D32',
  Cyndy: '#8E44AD',
  Kylie: '#C0392B',
};

// Tier columns, left → right, plus a catch-all lane for submissions with no tier.
const TIER_ORDER: TierKey[] = ['complete', 'assist', 'launch', 'gatekeeper', 'liberty'];

const NO_TIER = { key: 'none', name: 'Unassigned', band: 'No service tier set yet' };

// A Completed card auto-archives (hides from the board) once it's this old.
const ARCHIVE_MS = 60 * 24 * 60 * 60 * 1000;

function tierKeyOf(s: SubmissionRow): TierKey | 'none' {
  return s.tier && TIERS[s.tier as TierKey] ? (s.tier as TierKey) : 'none';
}

// Which board lane a submission belongs in: Completed / Archived override the tier lane
// once staff have marked it done (data.completedAt).
function laneOf(s: SubmissionRow): string {
  const ca = Number(s.data.completedAt) || 0;
  if (ca) return Date.now() - ca > ARCHIVE_MS ? 'archived' : 'completed';
  return tierKeyOf(s);
}

// Finished work — kept in its lane until dragged to Completed, but sunk to the bottom
// and never flagged as "aging" (age isn't actionable once it's done).
const TERMINAL_STATUSES = new Set(['declined', 'closed_lost', 'bound']);
const DAY_MS = 24 * 60 * 60 * 1000;


// The most recent time anything happened on a submission: staff advancing it, a message,
// an agent edit/upload, or — failing all that — when it was created. Drives age sorting so
// "stale" means genuinely untouched, not merely old.
function lastActivityAt(s: SubmissionRow): number {
  const t: number[] = [];
  if (s.created_at) { const c = Date.parse(s.created_at); if (!Number.isNaN(c)) t.push(c); }
  if (s.updated_at) { const u = Date.parse(s.updated_at); if (!Number.isNaN(u)) t.push(u); }
  if (typeof s.data.createdAt === 'number') t.push(s.data.createdAt);
  const led = (s.data as Record<string, unknown>).lastAgentEditAt;
  if (typeof led === 'number') t.push(led);
  if (typeof s.data.completedAt === 'number') t.push(s.data.completedAt);
  for (const h of Array.isArray(s.data.statusHistory) ? s.data.statusHistory : []) {
    if (typeof h?.at === 'number') t.push(h.at);
  }
  for (const m of Array.isArray(s.data.messages) ? s.data.messages : []) {
    if (typeof m?.at === 'number') t.push(m.at);
  }
  return t.length ? Math.max(...t) : 0;
}

// Lane ordering: unread (needs a look) on top, then active work stale-first (longest
// untouched rises), then finished cards at the bottom.
function laneRank(s: SubmissionRow): number {
  if (TERMINAL_STATUSES.has(s.status)) return 2;
  if (s.data.unread === true) return 0;
  return 1;
}
// Missing dates sort to the very bottom (Infinity) so blanks don't jump ahead of real ones.
function parseDateMs(v: unknown): number {
  if (typeof v !== 'string' || !v.trim()) return Infinity;
  const t = Date.parse(v);
  return Number.isNaN(t) ? Infinity : t;
}
function createdAtOf(s: SubmissionRow): number {
  if (s.created_at) {
    const c = Date.parse(s.created_at);
    if (!Number.isNaN(c)) return c;
  }
  return typeof s.data.createdAt === 'number' ? s.data.createdAt : 0;
}
// Comparator for the chosen sort. Always: unread on top, finished at the bottom; the
// ACTIVE middle group is ordered by the selected key (soonest-first for dates).
function laneComparator(mode: SortMode) {
  return (a: SubmissionRow, b: SubmissionRow): number => {
    const ra = laneRank(a);
    const rb = laneRank(b);
    if (ra !== rb) return ra - rb;
    if (ra !== 1) return lastActivityAt(b) - lastActivityAt(a); // unread & finished: most recent first
    switch (mode) {
      case 'needby':
        return parseDateMs(a.data.quoteNeedBy) - parseDateMs(b.data.quoteNeedBy);
      case 'expiration':
        return parseDateMs(a.data.policyExpiration) - parseDateMs(b.data.policyExpiration);
      case 'newest':
        return createdAtOf(b) - createdAtOf(a);
      default: // 'stale' — longest untouched first
        return lastActivityAt(a) - lastActivityAt(b);
    }
  };
}

// Days since last activity + an escalating tone; null for finished/completed cards.
function ageInfo(s: SubmissionRow): { days: number; tone: 'muted' | 'amber' | 'red' } | null {
  if (TERMINAL_STATUSES.has(s.status) || Number(s.data.completedAt)) return null;
  const last = lastActivityAt(s);
  if (!last) return null;
  const days = Math.floor((Date.now() - last) / DAY_MS);
  return { days, tone: days >= 14 ? 'red' : days >= 7 ? 'amber' : 'muted' };
}

// An active submission nobody has touched in 48h — flag it so someone gets back in.
const STALE_MS = 48 * 60 * 60 * 1000;
function isStale(s: SubmissionRow): boolean {
  if (TERMINAL_STATUSES.has(s.status) || Number(s.data.completedAt)) return false;
  const last = lastActivityAt(s);
  return last > 0 && Date.now() - last > STALE_MS;
}

// A compact "quote need-by" chip for active cards, colored by urgency (overdue/soon = red).
function needByInfo(s: SubmissionRow): { label: string; tone: 'muted' | 'amber' | 'red' } | null {
  if (TERMINAL_STATUSES.has(s.status) || Number(s.data.completedAt)) return null;
  const v = s.data.quoteNeedBy;
  if (typeof v !== 'string' || !v.trim()) return null;
  const t = Date.parse(v);
  if (Number.isNaN(t)) return null;
  const days = Math.ceil((t - Date.now()) / DAY_MS);
  const tone = days <= 1 ? 'red' : days <= 5 ? 'amber' : 'muted';
  return { label: `due ${new Date(t).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}`, tone };
}

function readView(): QueueView {
  return typeof localStorage !== 'undefined' && localStorage.getItem('commpass_queue_view') === 'grid'
    ? 'grid'
    : 'board';
}

function readSort(): SortMode {
  const v = typeof localStorage !== 'undefined' ? localStorage.getItem('commpass_queue_sort') : null;
  return v === 'needby' || v === 'expiration' || v === 'newest' ? v : 'stale';
}

export function StaffDashboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const update = useUpdateSubmission();
  const { data, isLoading, error } = useSubmissions();
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [view, setView] = useState<QueueView>(readView);
  const [showArchived, setShowArchived] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem('commpass_show_archived') === '1',
  );
  const [sortMode, setSortMode] = useState<SortMode>(readSort);

  // Drag-to-reassign / complete state. `drag.lane` is the lane the card came from.
  const [drag, setDrag] = useState<{ row: SubmissionRow; lane: string } | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  // Native HTML5 drag doesn't scroll the page, so a Completed lane far down (or the
  // tier lanes far up) is unreachable on a long board without zooming out. While a drag
  // is in progress, auto-scroll the window when the cursor nears the top/bottom edge —
  // a rAF loop keeps scrolling even when the cursor is held still at the edge.
  useEffect(() => {
    if (!drag) return;
    const EDGE = 100; // px from top/bottom that triggers scrolling
    const MAX = 26; // max px per frame
    let vy = 0;
    let raf = 0;
    const onOver = (e: globalThis.DragEvent) => {
      const y = e.clientY;
      const h = window.innerHeight;
      if (y < EDGE) vy = -Math.ceil((1 - y / EDGE) * MAX);
      else if (y > h - EDGE) vy = Math.ceil((1 - (h - y) / EDGE) * MAX);
      else vy = 0;
    };
    const tick = () => {
      if (vy) window.scrollBy(0, vy);
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener('dragover', onOver);
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('dragover', onOver);
      cancelAnimationFrame(raf);
    };
  }, [drag]);
  const [pending, setPending] = useState<{ row: SubmissionRow; to: TierKey } | null>(null);
  // Non-bound completion needs a reason: hold the card being completed + the chosen reason.
  const [completing, setCompleting] = useState<SubmissionRow | null>(null);
  const [completeReason, setCompleteReason] = useState('');
  const [completeNote, setCompleteNote] = useState('');
  // A completed drag must not also fire the card's click-through to the detail page.
  const didDrag = useRef(false);

  function pickView(v: QueueView) {
    setView(v);
    try {
      localStorage.setItem('commpass_queue_view', v);
    } catch {
      /* private-mode / storage disabled — the choice just won't persist */
    }
  }
  function pickShowArchived(on: boolean) {
    setShowArchived(on);
    try {
      localStorage.setItem('commpass_show_archived', on ? '1' : '0');
    } catch {
      /* ignore */
    }
  }
  function pickSort(s: SortMode) {
    setSortMode(s);
    try {
      localStorage.setItem('commpass_queue_sort', s);
    } catch {
      /* ignore */
    }
  }

  const rows = useMemo(() => {
    let out = data ?? [];
    if (statusFilter) out = out.filter((s) => s.status === statusFilter);
    if (q.trim()) {
      const needle = q.toLowerCase();
      out = out.filter(
        (s) =>
          s.id.toLowerCase().includes(needle) ||
          (s.business_name ?? '').toLowerCase().includes(needle) ||
          (s.submitter_email ?? '').toLowerCase().includes(needle) ||
          ((s.data.agencyName as string) ?? '').toLowerCase().includes(needle),
      );
    }
    // Hide archived unless the toggle is on.
    if (!showArchived) out = out.filter((s) => laneOf(s) !== 'archived');
    return out;
  }, [data, q, statusFilter, showArchived]);

  // Group the (already filtered) rows into their lanes for the board view.
  const lanes = useMemo(() => {
    const g: Record<string, SubmissionRow[]> = {
      complete: [],
      assist: [],
      launch: [],
      gatekeeper: [],
      liberty: [],
      none: [],
      completed: [],
      archived: [],
    };
    for (const s of rows) (g[laneOf(s)] ?? g.none)!.push(s);
    // Within each lane: unread on top, active work by the chosen sort, finished sunk.
    const cmp = laneComparator(sortMode);
    for (const key of Object.keys(g)) g[key]!.sort(cmp);

    type Col = { key: string; name: string; band: string; pct: number; kind: 'tier' | 'none' | 'completed' | 'archived' };
    const cols: Col[] = TIER_ORDER.map((k) => ({
      key: k as string,
      name: TIERS[k].name,
      band: TIERS[k].band,
      pct: TIERS[k].member, // the agent's commission share (0 = no split, e.g. Gatekeeper/Liberty)
      kind: 'tier',
    }));
    if (g.none!.length) cols.push({ ...NO_TIER, pct: 0, kind: 'none' });
    // Completed lane is always a visible drop target; Archived only when toggled on.
    cols.push({ key: 'completed', name: 'Completed', band: 'Done — archives after 60 days', pct: 0, kind: 'completed' });
    if (showArchived) cols.push({ key: 'archived', name: 'Archived', band: 'Completed over 60 days ago', pct: 0, kind: 'archived' });
    return { g, cols };
  }, [rows, showArchived, sortMode]);

  // Persist a manual tier reassignment (tier → tier drop) + log it to the timeline.
  async function reassign(row: SubmissionRow, to: TierKey) {
    const from = tierKeyOf(row);
    const history = Array.isArray(row.data.tierHistory) ? row.data.tierHistory : [];
    const nextData = {
      ...row.data,
      tier: to,
      tierHistory: [...history, { from: from === 'none' ? undefined : from, to, at: Date.now() }],
    };
    try {
      await update.mutateAsync({ id: row.id, data: nextData });
      toast(`Moved ${row.business_name || 'submission'} to ${TIERS[to].name}`);
    } catch {
      toast('Could not move — please try again.');
    }
  }

  // Assign (or clear) the AAS owner shown on the board.
  async function assign(row: SubmissionRow, name: string) {
    try {
      await update.mutateAsync({ id: row.id, data: { ...row.data, assignedTo: name || undefined } });
      toast(name ? `Assigned to ${name}` : 'Owner cleared');
    } catch {
      toast('Could not update the owner — please try again.');
    }
  }

  // Drop onto the Completed lane: stamp completedAt (leaves its tier lane). A reason is
  // recorded whenever the submission didn't bind, and shows in the timeline + on the card.
  async function markCompleted(row: SubmissionRow, reason?: string) {
    const now = Date.now();
    const nextData: SubmissionData = { ...row.data, completedAt: now };
    if (reason) {
      nextData.completedReason = reason;
      const hist = Array.isArray(row.data.statusHistory) ? row.data.statusHistory : [];
      nextData.statusHistory = [...hist, { status: row.status, at: now, note: `Completed: ${reason}` }];
    }
    try {
      await update.mutateAsync({ id: row.id, data: nextData });
      toast(`${row.business_name || 'Submission'} marked completed — archives in 60 days`);
    } catch {
      toast('Could not update — please try again.');
    }
  }

  // Complete a card without dragging — the board is long and native drag won't scroll,
  // so a button on the card is the reliable path. Mirrors the Completed-lane drop: a
  // bound deal completes outright; anything else asks for a reason first.
  function completeCard(row: SubmissionRow) {
    if (Number(row.data.completedAt)) return; // already completed
    if (row.status === 'bound') {
      void markCompleted(row);
    } else {
      setCompleteReason('');
      setCompleteNote('');
      setCompleting(row);
    }
  }

  // Drop a Completed/Archived card back onto a tier lane: clear completedAt + set the tier.
  async function reactivate(row: SubmissionRow, to: TierKey) {
    const nextData = { ...row.data, tier: to };
    delete (nextData as { completedAt?: number }).completedAt;
    try {
      await update.mutateAsync({ id: row.id, data: nextData });
      toast(`Reactivated ${row.business_name || 'submission'} into ${TIERS[to].name}`);
    } catch {
      toast('Could not update — please try again.');
    }
  }

  const DROP_OK = (kind: string) => kind === 'tier' || kind === 'completed';

  function onColDragOver(e: DragEvent, colKey: string, kind: string) {
    if (!drag || !DROP_OK(kind) || drag.lane === colKey) return;
    e.preventDefault();
    if (dragOverKey !== colKey) setDragOverKey(colKey);
  }

  function onColDrop(e: DragEvent, colKey: string, kind: string) {
    e.preventDefault();
    const d = drag;
    setDrag(null);
    setDragOverKey(null);
    if (!d || !DROP_OK(kind) || d.lane === colKey) return;

    if (kind === 'completed') {
      // Bound = the reason is obvious (it bound). Anything else needs a stated reason.
      if (d.row.status === 'bound') {
        void markCompleted(d.row);
      } else {
        setCompleteReason('');
        setCompleteNote('');
        setCompleting(d.row);
      }
      return;
    }
    // Dropping onto a tier lane.
    if (d.lane === 'completed' || d.lane === 'archived') {
      void reactivate(d.row, colKey as TierKey);
      return;
    }
    // Tier → tier: confirm (it changes the commission split).
    setPending({ row: d.row, to: colKey as TierKey });
  }

  const splitLine = pending
    ? pending.to === 'gatekeeper' || pending.to === 'liberty'
      ? `Handled by AAS — no commission split (${TIERS[pending.to].name}).`
      : `New commission split: ${TIERS[pending.to].member}% agent · ${TIERS[pending.to].aas}% AAS.`
    : '';

  return (
    <div className="wrap wrap-wide">
      <StaffTabs />
      <div className="page-head">
        <div>
          <div className="page-title">Queue</div>
          <div className="page-sub">Every COMM-PASS submission across the pipeline.</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/settings')}>
          Settings
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="finput"
          placeholder="Search by insured, agency, email, or code…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 380 }}
        />
        <select
          className="finput"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ maxWidth: 220 }}
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
        {view === 'board' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: 'var(--muted)', whiteSpace: 'nowrap' }}>Sort:</span>
            <div className="view-toggle" role="group" aria-label="Sort submissions">
              {SORT_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  className={sortMode === o.key ? 'on' : ''}
                  aria-pressed={sortMode === o.key}
                  onClick={() => pickSort(o.key)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginLeft: 'auto' }}>
          {(() => {
            const n = rows.filter(isStale).length;
            return n > 0 ? (
              <span
                title="Active submissions with no activity in 48h+"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  background: '#FEE2E2',
                  color: '#B91C1C',
                  border: '1px solid #DC2626',
                  borderRadius: 20,
                  padding: '2px 10px',
                  fontSize: 12,
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                }}
              >
                ⚑ {n} need attention
              </span>
            ) : null;
          })()}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={showArchived} onChange={(e) => pickShowArchived(e.target.checked)} />
            Show archived
          </label>
          <div className="view-toggle" role="tablist" aria-label="Queue layout">
            <button type="button" className={view === 'board' ? 'on' : ''} aria-pressed={view === 'board'} onClick={() => pickView('board')}>
              Board
            </button>
            <button type="button" className={view === 'grid' ? 'on' : ''} aria-pressed={view === 'grid'} onClick={() => pickView('grid')}>
              Grid
            </button>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="empty">
          <p>Loading submissions…</p>
        </div>
      )}
      {error && (
        <div className="empty">
          <h3>Couldn't load submissions</h3>
          <p>{(error as Error).message}. Check that Supabase is configured and you're signed in as staff.</p>
        </div>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <div className="empty">
          <h3>No submissions yet</h3>
          <p>New agent submissions will appear here.</p>
        </div>
      )}

      {!isLoading && !error && rows.length > 0 && view === 'grid' && (
        <div className="card-grid">
          {rows.map((s) => (
            <SubCard key={s.id} s={s} onOpen={() => navigate(`/s/${s.id}`)} />
          ))}
        </div>
      )}

      {!isLoading && !error && rows.length > 0 && view === 'board' && (
        <div className="kb-board">
          {lanes.cols.map((col) => {
            const cards = lanes.g[col.key] ?? [];
            return (
              <section
                key={col.key}
                className={`kb-col t-${col.key}${col.kind === 'completed' ? ' kb-collapsible' : ''}${dragOverKey === col.key ? ' drag-over' : ''}`}
                onDragOver={(e) => onColDragOver(e, col.key, col.kind)}
                onDrop={(e) => onColDrop(e, col.key, col.kind)}
              >
                <div className="kb-col-head">
                  <div className="kb-col-title">
                    <span className="kb-col-name">
                      {col.name}
                      {col.pct > 0 && <span className="kb-col-pct">{col.pct}%</span>}
                    </span>
                    <span className="kb-col-count">{cards.length}</span>
                  </div>
                  <div className="kb-col-band">{col.band}</div>
                  {col.kind === 'completed' && <div className="kb-collapse-hint">hover to expand</div>}
                </div>
                <div className="kb-col-body">
                  {cards.length === 0 ? (
                    <div className="kb-col-empty">None</div>
                  ) : (
                    cards.map((s) => (
                      <KanbanCard
                        key={s.id}
                        s={s}
                        dragging={drag?.row.id === s.id}
                        onOpen={() => {
                          if (didDrag.current) return;
                          navigate(`/s/${s.id}`);
                        }}
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', s.id);
                          e.dataTransfer.effectAllowed = 'move';
                          didDrag.current = true;
                          setDrag({ row: s, lane: col.key });
                        }}
                        onDragEnd={() => {
                          setDrag(null);
                          setDragOverKey(null);
                          setTimeout(() => {
                            didDrag.current = false;
                          }, 0);
                        }}
                        onAssign={(name) => void assign(s, name)}
                        onComplete={() => completeCard(s)}
                      />
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {pending && (
        <Modal
          title="Reassign service tier"
          onClose={() => setPending(null)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setPending(null)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={update.isPending}
                onClick={async () => {
                  await reassign(pending.row, pending.to);
                  setPending(null);
                }}
              >
                {update.isPending ? 'Moving…' : `Move to ${TIERS[pending.to].name}`}
              </button>
            </>
          }
        >
          <p style={{ fontSize: 14, lineHeight: 1.55, marginBottom: 10 }}>
            Move <b>{pending.row.business_name || 'this submission'}</b> from{' '}
            <b>{tierKeyOf(pending.row) === 'none' ? 'Unassigned' : TIERS[tierKeyOf(pending.row) as TierKey].name}</b>{' '}
            to <b>{TIERS[pending.to].name}</b>?
          </p>
          <div className="banner" style={{ margin: 0 }}>
            <span style={{ fontSize: 13 }}>{splitLine}</span>
          </div>
        </Modal>
      )}

      {completing && (() => {
        const needsNote = completeReason === 'Other';
        const finalReason = needsNote ? completeNote.trim() : completeReason;
        const ready = !!completeReason && (!needsNote || completeNote.trim().length > 0);
        return (
          <Modal
            title="Why is this being completed?"
            onClose={() => setCompleting(null)}
            footer={
              <>
                <button className="btn btn-ghost" onClick={() => setCompleting(null)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!ready || update.isPending}
                  onClick={async () => {
                    const row = completing;
                    setCompleting(null);
                    await markCompleted(row, finalReason);
                  }}
                >
                  {update.isPending ? 'Completing…' : 'Complete submission'}
                </button>
              </>
            }
          >
            <p style={{ fontSize: 14, lineHeight: 1.55, marginBottom: 12 }}>
              <b>{completing.business_name || 'This submission'}</b> didn't bind. Pick a reason so
              we always know why it was closed — it shows on the card and in the timeline.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {COMPLETE_REASONS.map((r) => (
                <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 14, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="complete-reason"
                    checked={completeReason === r}
                    onChange={() => setCompleteReason(r)}
                  />
                  {r}
                </label>
              ))}
            </div>
            {needsNote && (
              <input
                className="finput"
                style={{ marginTop: 10 }}
                placeholder="Briefly, what happened?"
                value={completeNote}
                onChange={(e) => setCompleteNote(e.target.value)}
                autoFocus
              />
            )}
          </Modal>
        );
      })()}
    </div>
  );
}

// Staff-only completeness grade badge (A+…F, color by band).
function GradeBadge({ data, large }: { data: SubmissionData; large?: boolean }) {
  const g = gradeSubmission(data);
  return (
    <span
      className={`grade-badge g-${g.band}${large ? ' grade-lg' : ''}`}
      title={`Submission completeness: ${g.grade} (${g.score}/100) — staff only`}
    >
      {g.grade}
    </span>
  );
}

function SubCard({ s, onOpen }: { s: SubmissionRow; onOpen: () => void }) {
  const unread = s.data.unread === true;
  return (
    <div className={`sub-card${unread ? ' has-unread' : ''}`} onClick={onOpen}>
      <div className="sub-card-top">
        <div className="sub-insured">{s.business_name || 'Unnamed insured'}</div>
        <StatusPill status={s.status} reviewer />
      </div>
      <div className="sub-meta">{(s.data.agencyName as string) || '—'}</div>
      <div className="sub-card-bottom">
        <div className="sub-id">{s.id}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          {unread && (
            <span className="unread-flag" title="New message from the agent">
              New message
            </span>
          )}
          <GradeBadge data={s.data} />
        </div>
      </div>
    </div>
  );
}

function KanbanCard({
  s,
  onOpen,
  onDragStart,
  onDragEnd,
  dragging,
  onAssign,
  onComplete,
}: {
  s: SubmissionRow;
  onOpen: () => void;
  onDragStart: (e: DragEvent) => void;
  onDragEnd: (e: DragEvent) => void;
  dragging: boolean;
  onAssign: (name: string) => void;
  onComplete: () => void;
}) {
  const unread = s.data.unread === true;
  const completedAt = Number(s.data.completedAt) || 0;
  const age = ageInfo(s);
  const ageColor = age?.tone === 'red' ? '#C0392B' : age?.tone === 'amber' ? '#B7791F' : 'var(--muted)';
  const needBy = needByInfo(s);
  const needByColor = needBy?.tone === 'red' ? '#C0392B' : needBy?.tone === 'amber' ? '#B7791F' : 'var(--muted)';
  const assignedTo = (s.data.assignedTo as string) || '';
  const stale = isStale(s);
  return (
    <div
      className={`kb-card${unread ? ' has-unread' : ''}${dragging ? ' dragging' : ''}`}
      style={stale ? { borderLeft: '3px solid #DC2626' } : undefined}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      title="Drag to another lane to reassign, complete, or reactivate"
    >
      <div className="kb-card-head">
        <div className="kb-card-name">{s.business_name || 'Unnamed insured'}</div>
        <GradeBadge data={s.data} />
      </div>
      <div className="kb-card-meta">{(s.data.agencyName as string) || '—'}</div>
      <StatusPill status={s.status} reviewer />
      {stale && (
        <div
          title="No activity in over 48 hours — get back in and move it forward"
          style={{
            marginTop: 6,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            background: '#FEE2E2',
            color: '#B91C1C',
            border: '1px solid #DC2626',
            borderRadius: 6,
            padding: '2px 8px',
            fontSize: 11,
            fontWeight: 800,
          }}
        >
          ⚑ Needs attention
        </div>
      )}
      <div
        style={{ marginTop: 8 }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <select
          value={assignedTo}
          onChange={(e) => onAssign(e.target.value)}
          title="Assign an owner — visible to the whole team"
          style={{
            width: '100%',
            fontSize: 12,
            fontWeight: 700,
            padding: '3px 6px',
            borderRadius: 6,
            border: `1px solid ${assignedTo ? ASSIGNEE_COLOR[assignedTo] ?? 'var(--border)' : 'var(--border)'}`,
            color: assignedTo ? ASSIGNEE_COLOR[assignedTo] ?? 'var(--text)' : 'var(--muted)',
            background: 'var(--card)',
            cursor: 'pointer',
          }}
        >
          <option value="">＋ Assign owner</option>
          {ASSIGNEES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
      {completedAt === 0 && (
        <div
          style={{ marginTop: 8 }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            className="btn btn-ghost btn-sm"
            style={{ width: '100%' }}
            onClick={onComplete}
            title="Mark this submission completed — no dragging needed"
          >
            ✓ Complete
          </button>
        </div>
      )}
      {completedAt > 0 && (
        <div className="kb-card-meta" style={{ color: '#6E56A8', marginTop: 6 }}>
          ✓ Completed {new Date(completedAt).toLocaleDateString()}
          {s.data.completedReason ? ` — ${s.data.completedReason}` : ''}
        </div>
      )}
      <div className="kb-card-foot">
        <span className="kb-card-id">{s.id}</span>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {needBy && (
            <span
              title={`Quote need-by ${(s.data.quoteNeedBy as string) || ''}`}
              style={{ fontSize: 11, fontWeight: 700, color: needByColor, whiteSpace: 'nowrap' }}
            >
              {needBy.label}
            </span>
          )}
          {age && (
            <span
              title={`${age.days} day${age.days === 1 ? '' : 's'} since last activity`}
              style={{ fontSize: 11, fontWeight: 700, color: ageColor, whiteSpace: 'nowrap' }}
            >
              {age.tone !== 'muted' ? '⏳ ' : ''}
              {age.days <= 0 ? 'today' : `${age.days}d`}
            </span>
          )}
          {unread && (
            <span className="unread-flag" title="New message from the agent">
              New
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
